import { PaginationState, SortingState } from '@tanstack/react-table';
import { z } from 'zod';

export interface PagedResult<T> {
	data: T[];
	total: number;
}

export type VelocityFeedAuditTab = 'active' | 'hidden' | 'resolved';

export const all = async (
	globalSearch: string = '',
	tab: VelocityFeedAuditTab = 'active',
	tabSearch: string = '',
	startDate?: Date | null,
	endDate?: Date | null,
	sorting?: SortingState,
	pagination: PaginationState = { pageIndex: 0, pageSize: 10 },
	categories: string[] = [],
): Promise<PagedResult<VelocityError>> => {
	const params = new URLSearchParams();

	if (globalSearch) params.append('globalSearch', globalSearch);
	if (tab) params.append('tab', tab);
	if (tabSearch) params.append('tabSearch', tabSearch);
	if (startDate) params.append('startDate', startDate.toISOString());
	if (endDate) params.append('endDate', endDate.toISOString());
	categories.forEach((category) => params.append('categories', category));

	params.append('pageIndex', (pagination.pageIndex + 1).toString());
	params.append('pageSize', pagination.pageSize.toString());

	if (sorting && sorting.length > 0) {
		const sort = sorting[0];
		params.append('sortBy', sort.id);
		params.append('sortDirection', sort.desc ? 'desc' : 'asc');
	}
	const response = await fetch(`/VelocityFeedAudit/?${params}`);
	if (!response.ok) throw new Error('Failed to fetch Velocity Feed Audit data');
	return await response.json();
};

export const SetExceptionsHidden = async (
	ids: number[],
	hide: boolean,
): Promise<void> => {
	const res = await fetch(`/VelocityFeedAudit/SetExceptionsHidden`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ exceptionIds: ids, hide }),
	});

	if (!res.ok) {
		let message = 'Failed to update exception visibility';
		try {
			const body = await res.json();
			if (typeof body?.message === 'string' && body.message.trim().length > 0) {
				message = body.message;
			}
		} catch {
			// Keep default message when the response body is not JSON.
		}

		throw new Error(message);
	}
};

export const getCategoryOptions = async (): Promise<string[]> => {
	const response = await fetch(
		`/VelocityFeedAudit/Category/Options`,
	);
	if (!response.ok) throw new Error('Failed to fetch category options');
	return await response.json();
};

export const getTransactionQueue = async (
	searchTerm: string = '',
): Promise<PagedResult<VelocityTransactionQueueItem>> => {
	const params = new URLSearchParams();

	if (searchTerm) params.append('searchTerm', searchTerm);

	const response = await fetch(`/VelocityFeedAudit/transaction-queue?${params}`);
	if (!response.ok) throw new Error('Failed to fetch transaction queue data');
	return await response.json();
};

export const VelocityErrorSchema = z.object({
	id: z.number().nullable(),
	pipsId: z.string().nullable(),
	level1Org: z.number().nullable(),
	category: z.string().nullable(),
	name: z.string().nullable(),
	error: z.string().nullable(),
	errorDate: z.date().nullable(),
	isResolved: z.boolean().nullable(),
	isHidden: z.boolean().nullable(),
});

export type VelocityError = z.infer<typeof VelocityErrorSchema>;

export const VelocityTransactionQueueItemSchema = z.object({
	level1Org: z.number().nullable(),
	description: z.string().nullable(),
	policyId: z.number().nullable(),
	policyNumber: z.string().nullable(),
	effectiveDate: z.string().nullable(),
	providerNumber: z.string().nullable(),
	providerName: z.string().nullable(),
	producerNumber: z.string().nullable(),
	producerName: z.string().nullable(),
});

export type VelocityTransactionQueueItem = z.infer<
	typeof VelocityTransactionQueueItemSchema
>;
